
import { supabase } from '../client';
import { UserType } from '../types';

// Function to fetch all users
export const fetchAllUsers = async (): Promise<UserType[]> => {
  try {
    // Get profiles from the profiles table
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('*');
    
    if (profilesError) {
      console.error('Error fetching profiles:', profilesError);
      throw new Error(`Failed to fetch profiles: ${profilesError.message}`);
    }
    
    // Get user emails via RPC function - this will handle permissions automatically
    // The RPC function will return all emails for admins, or just the current user's email for regular users
    const { data: emails, error: rpcError } = await supabase
      .rpc('get_all_user_emails');
    
    // Create a map of user IDs to emails
    const emailMap = new Map();
    
    if (!rpcError && emails) {
      emails.forEach((item: { id: string; email: string }) => {
        emailMap.set(item.id, item.email);
      });
      console.log('Successfully fetched user emails via RPC function:', emails.length);
    } else {
      console.error('Error fetching emails via RPC:', rpcError);
    }
    
    // Map profiles to UserType objects, including emails from the map
    return (profiles || []).map(profile => ({
      id: profile.id,
      email: emailMap.get(profile.id) || '', 
      firstName: profile.first_name || '',
      lastName: profile.last_name || '',
      phone: profile.phone || '',
      isAdmin: profile.is_admin || false,
      createdAt: profile.created_at || new Date().toISOString()
    }));
  } catch (error: any) {
    console.error('Error processing users:', error);
    throw new Error(`Failed to process users: ${error.message}`);
  }
};

// Function to search users by name
export const searchUsers = async (searchTerm: string): Promise<UserType[]> => {
  try {
    if (!searchTerm || searchTerm.trim().length === 0) {
      return [];
    }
    
    console.log(`Searching users with term: ${searchTerm}`);
    
    // Search in profiles table by first or last name
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%`)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error searching profiles:', error);
      throw new Error(`Failed to search profiles: ${error.message}`);
    }
    
    // Get user emails via the same RPC function
    const { data: emails, error: rpcError } = await supabase
      .rpc('get_all_user_emails');
    
    // Create a map of user IDs to emails
    const emailMap = new Map();
    
    if (!rpcError && emails) {
      emails.forEach((item: { id: string; email: string }) => {
        emailMap.set(item.id, item.email);
      });
    } else {
      console.error('Error fetching emails via RPC:', rpcError);
    }
    
    console.log(`Search returned ${data.length} results`);
    return (data || []).map(profile => ({
      id: profile.id,
      email: emailMap.get(profile.id) || '',
      firstName: profile.first_name || '',
      lastName: profile.last_name || '',
      phone: profile.phone || '',
      isAdmin: profile.is_admin || false,
      createdAt: profile.created_at || new Date().toISOString()
    }));
  } catch (error: any) {
    console.error('Error processing user search:', error);
    throw new Error(`Failed to process user search: ${error.message}`);
  }
};
